<template>
  <v-scale-transition>
    <panel
      :breadcrumbs="breadcrumbs"
      :title="title"
    >
      <!-- ALERT -->
      <v-alert
        v-if="executeAlertMessage"
        :type="executeAlertType"
        :value="true"
        dismissible
        class="mb-4"
      >
        {{ executeAlertMessage }}
      </v-alert>

      <!-- UPLOAD CARD -->
      <v-card
        class="pa-5 mb-4"
        outlined
      >
        <div class="headline font-weight-bold mb-2">
          Upload Attendance Excel
        </div>

        <div class="grey--text mb-4">
          Upload dealer course attendance file (.xlsx / .xls)
        </div>

        <v-layout wrap>
          <!-- DATE -->
          <v-flex
            xs12
            md4
            class="mb-4"
          >
            <v-menu
              ref="menu"
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template #activator="{ on }">
                <v-text-field
                  v-model="selectedDate"
                  label="Attendance Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-on="on"
                />
              </template>

              <v-date-picker
                v-model="selectedDate"
                @input="dateMenu = false"
              />
            </v-menu>
          </v-flex>
          <!-- FILE -->
          <v-flex xs12>
            <v-layout
              align-center
              wrap
            >
              <!-- FILE -->
              <v-flex xs8>
                <div class="d-flex align-center">
                  <input
                    ref="fileInput"
                    type="file"
                    accept=".xlsx,.xls"
                    style="display: none"
                    @change="handleFileUpload"
                  >

                  <v-btn
                    color="primary"
                    large
                    @click="$refs.fileInput.click()"
                  >
                    <v-icon left>
                      mdi-file-excel
                    </v-icon>

                    Choose Excel File
                  </v-btn>

                  <div
                    v-if="file"
                    class="ml-4"
                  >
                    <v-chip
                      color="success"
                      text-color="white"
                      small
                    >
                      <v-icon
                        left
                        small
                      >
                        mdi-check-circle
                      </v-icon>

                      {{ file.name }}
                    </v-chip>
                  </div>

                  <div
                    v-else
                    class="ml-4 grey--text"
                  >
                    No file selected
                  </div>
                </div>
              </v-flex>

              <!-- PARSE BUTTON -->
              <v-flex
                xs4
                class="text-right"
              >
                <v-btn
                  color="primary"
                  large
                  :disabled="!file"
                  @click="parseFile"
                >
                  <v-icon left>
                    mdi-file-excel
                  </v-icon>

                  Parse Excel
                </v-btn>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-card>

      <!-- TABLE -->
      <v-card
        v-if="tableData.length"
        class="mb-4"
      >
        <v-card-title
          class="font-weight-bold"
        >
          Attendance Preview

          <v-spacer />

          <v-chip
            color="primary"
            text-color="white"
            small
          >
            {{ tableData.length }} Records
          </v-chip>
        </v-card-title>

        <v-data-table
          :headers="tableHeaders"
          :items="tableData"
          class="elevation-0"
          rows-per-page-text="Rows"
          :rows-per-page-items="[10,25,50,100]"
        >
          <template
            slot="items"
            slot-scope="props"
          >
            <td>
              <v-chip
                small
                color="grey darken-2"
                text-color="white"
              >
                {{ props.item.dealerID }}
              </v-chip>
            </td>
          </template>
        </v-data-table>
      </v-card>

      <!-- SUBMIT BUTTON -->
      <div
        v-if="tableData.length"
        class="text-right"
      >
        <v-btn
          color="success"
          large
          :loading="loading"
          :disabled="loading"
          @click="submitAttendance"
        >
          <v-icon left>
            mdi-cloud-upload
          </v-icon>

          Submit Attendance
        </v-btn>
      </div>
    </panel>
  </v-scale-transition>
</template>

<script>
import { createPostParams } from '@/rest'
import * as XLSX from 'xlsx'

const pageTitle =
  'Upload Dealer Course Attendance'

export default {

  components: {
    Panel: () => import(
      '@/components/Panel'
    ),
  },

  data () {
    return {
      selectedDate: '',
      dateMenu: false,
      file: null,

      tableData: [],

      loading: false,

      executeAlertType: 'info',

      executeAlertMessage: null,

      breadcrumbs: [
        {
          text: 'DMT Kits', disabled: false, to: '/support/dmt_kits',
        },
        {
          text: pageTitle, disabled: true,
        },
      ],

      tableHeaders: [
        {
          text: 'Dealer ID',
          value: 'dealerID',
        },
      ],

    }
  },

  computed: {

    title () {
      return pageTitle
    },

  },

  methods: {

    async submitAttendance () {
      const params =
        createPostParams({
          action:
            'uploadDealerAttendance',

          payload: {
            attendance:
              this.tableData,
            attendanceDate: this.selectedDate,
          },
        })

      try {
        this.loading = true

        const response =
  await this.$rest.post(
    'postAction.php',
    params,
  )

        this.$nextTick(() => {
          this.executeAlertType =
            'success'

          const summary =
  response.data.summary

          const issues =
  response.data.issues || []

          const profileNotExist =
  issues.filter(
    item =>
      item.includes(
        'profile does not exist',
      ),
  )

          const alreadyExist =
  issues.filter(
    item =>
      item.includes(
        'dealerID already exist in KTP',
      ),
  )

          if (issues.length) {
            this.executeAlertType =
    'warning'

            this.executeAlertMessage =

   'Success: ' +
summary.successCount +

'\nTotal Issues: ' +
issues.length +

    '\n\nProfile Not Exist (' +
    summary.profileNotExistCount +
    '):\n' +

    (
      profileNotExist.length
        ? profileNotExist.join('\n')
        : 'None'
    ) +

    '\n\nAlready Exist (' +
    summary.alreadyExistCount +
    '):\n' +

    (
      alreadyExist.length
        ? alreadyExist.join('\n')
        : 'None'
    )
          } else {
            this.executeAlertType =
    'success'

            this.executeAlertMessage =

    'Attendance uploaded successfully.' +

    '\n\nSuccess: ' +
    summary.successCount
          }

          this.tableData = []

          this.file = null
        })
      } catch (e) {
        this.$nextTick(() => {
          this.executeAlertType =
            'error'

          this.executeAlertMessage =
            e.message
        })
      } finally {
        this.loading = false
      }
    },

    formatExcelDate (excelDate) {
      if (!excelDate) {
        return ''
      }

      const date = new Date(
        (excelDate - 25569) *
        86400 *
        1000,
      )

      return date.toLocaleDateString(
        'en-GB',
      )
    },

    handleFileUpload (event) {
      this.file =
        event.target.files[0]
    },

    parseFile () {
      if (!this.file) {
        return
      }

      const reader =
        new FileReader()

      reader.onload = (e) => {
        const data =
          new Uint8Array(
            e.target.result,
          )

        const workbook =
          XLSX.read(data, {
            type: 'array',
          })

        const firstSheetName =
          workbook.SheetNames[0]

        const worksheet =
          workbook.Sheets[
            firstSheetName
          ]

        const json =
          XLSX.utils.sheet_to_json(
            worksheet,
            {
              header: 1,
            },
          )

        this.processData(json)
      }

      reader.readAsArrayBuffer(
        this.file,
      )
    },

    processData (data) {
      if (!data.length) {
        return
      }

      this.tableData = data
        .filter(row => row.length)
        .map(row => {
          return {
            dealerID: row[0] || '',
          }
        })
    },

  },

}
</script>
